import { List } from '@gonfalon/immutable-shim/immutableShim';
import { toJS } from '@gonfalon/legacy-immutable/immutableUtils';

import { idOrKey } from '#utils/flags/idOrKey';
import type { Flag } from '#utils/flags/records/Flag';
import type { Rule } from '#utils/flags/records/Rule';
import type { SemanticInstruction } from '#utils/instructions/shared/types';

import { RuleInstructionKind } from './constants';
import { convertRuleToAggregateRule } from './convertRuleToAggregateRule';
import type {
  AddRuleSemanticInstruction,
  AddRuleWithProgressiveRolloutSemanticInstruction,
  AggregateRule,
  ReorderRulesSemanticInstruction,
} from './types';

export const sortRulesByTargetingOrder = ({
  flag,
  envKey,
  instructions,
}: {
  flag: Flag;
  envKey: string;
  instructions: SemanticInstruction[];
}): Array<AggregateRule | undefined> => {
  const sortedRules: Array<AggregateRule | undefined> = [];
  let reorderRulesInstruction: ReorderRulesSemanticInstruction | undefined;
  const addRuleIns: Array<AddRuleSemanticInstruction | AddRuleWithProgressiveRolloutSemanticInstruction> = [];
  // Since the user was able to re-order rules, so we must have a list of rules for this environment
  const existingRules = flag.getRules(envKey) || List<Rule>();

  instructions.forEach((ins) => {
    if (ins.kind === RuleInstructionKind.REORDER_RULES) {
      reorderRulesInstruction = ins;
    }
    if (ins.kind === RuleInstructionKind.ADD_RULE) {
      addRuleIns.push(ins);
    }
  });

  if (!reorderRulesInstruction) {
    return existingRules.map((r: Rule) => convertRuleToAggregateRule(r, flag.variations)).toArray();
  }

  reorderRulesInstruction.ruleIds.forEach((ruleId) => {
    let rule: AggregateRule | undefined;
    existingRules.forEach((existingRule: Rule) => {
      if (ruleId === idOrKey(existingRule)) {
        rule = convertRuleToAggregateRule(existingRule, flag.variations);
      }
    });
    sortedRules.push(rule);
  });

  addRuleIns.forEach((ins) => {
    const { beforeRuleId } = ins;
    if (!beforeRuleId) {
      sortedRules.push(toJS(ins) as AggregateRule);
      return;
    }
    const beforeRuleIdx = sortedRules.findIndex((rule) => rule && beforeRuleId === idOrKey(rule));
    beforeRuleIdx > -1
      ? sortedRules.splice(beforeRuleIdx, 0, toJS(ins) as AggregateRule)
      : sortedRules.push(toJS(ins) as AggregateRule);
  });
  return sortedRules;
};
